import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AsyncStatus, GameSystems, ICampaignTrait, IGameRule } from "../types";
import { getQuestRules } from "./questSlice";

interface RulesState {
  rules: IGameRule[];
  traits: (ICampaignTrait & { gameSystem: GameSystems })[];
  status: AsyncStatus;
}

const initialState: RulesState = {
  rules: [],
  traits: [],
  status: AsyncStatus.idle
};

export const getGameRules = createAsyncThunk(
  "rules/getGameRules",
  async (param: { gameSystem: GameSystems; reset: boolean }) => {
    const { gameSystem, reset } = param;
    const res = await fetch("/api/rules/common/" + gameSystem);
    if (!res.ok) {
      throw new Error(`Failed to fetch rules: ${res.statusText}`);
    }
    const { rules, traits } = await res.json();
    return {
      rules: rules.map((rule) => ({
        id: rule.id,
        name: rule.name,
        description: rule.description,
        coreType: rule.coreType,
        gameSystem,
        displayCoreRuleForSystem: rule.displayCoreRuleForSystem,
      })),
      traits: traits.map((x) => ({ ...x, gameSystem })),
      gameSystem,
      reset
    };
  }
);

const rulesSlice = createSlice({
  name: "rules",
  initialState: initialState,
  reducers: {
    setRules(state, action) {
      state.rules = action.payload;
      state.status = AsyncStatus.succeeded;
    }
  },
  selectors: {
    selectGameRules(state) {
      return state.rules;
    },
    selectCampaignTraits(state) {
      return state.traits;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(getGameRules.pending, (state) => {
        return {
          ...state,
          rulesStatus: AsyncStatus.loading
        };
      })
      .addCase(getGameRules.fulfilled, (state, action) => {
        const { rules, traits, gameSystem, reset } = action.payload;
        const existingRules = reset ? [] : state.rules.filter((x) => x.gameSystem === gameSystem);
        const existingTraits = reset ? [] : state.traits.filter((x) => x.gameSystem === gameSystem);
        return {
          ...state,
          rules: [...existingRules, ...rules],
          traits: [...existingTraits, ...traits],
          gameSystem,
          status: AsyncStatus.succeeded
        };
      });
    builder.addCase(getQuestRules.fulfilled, (state, action) => {
      const { rules } = action.payload;
      for (const rule of rules) {
        if (!state.rules.some((x) => x.name === rule.name)) {
          state.rules.push(rule);
        }
      }
    });
  }
});

// Action creators are generated for each case reducer function
export const { setRules } = rulesSlice.actions;

export const { selectCampaignTraits, selectGameRules } = rulesSlice.selectors;

export default rulesSlice.reducer;
